import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, switchMap } from 'rxjs';
import { environment } from '../../../../environments/environment';

import { 
  User, 
  UserRequest, 
  UserUpdateStatusRequest,
  UserDetailResponse,
  UserUpdateProfilePictureRequest,
  UpdateUserProfileRequest,
  UserProfileResponse,
  ViewUserProfileResponse
  } from '../models/user.model';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private apiUrl = `${environment.apiURL}`;

  constructor(private http: HttpClient) { }

  get baseApiUrl(): string {
    return this.apiUrl;
  }

  // Get all users
  getUsers(roleNames: string[] = [], status: string = ''): Observable<User[]> {
    const payload = {
      roleNames: roleNames,
      status: status
    };
    return this.http.post<User[]>(`${this.apiUrl}/usermanagement/list`, payload);
  }

  // Get user by ID - returns hierarchical data based on role
  getUserById(userId: string): Observable<UserDetailResponse> {
    return this.http.get<UserDetailResponse>(`${this.apiUrl}/usermanagement/get/${userId}`);
  }

    // Create new user
  createUser(userRequest: UserRequest): Observable<string> {
    return this.http.post<string>(`${this.apiUrl}/usermanagement/add`, userRequest);
  }

  // Update existing user
  updateUser(userId: string, userRequest: UserRequest): Observable<string> {
    return this.http.put<string>(`${this.apiUrl}/usermanagement/edit/${userId}`, userRequest);
  }

  // Delete user
  deleteUser(userId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.apiUrl}/usermanagement/delete/${userId}`);
  }

  // Update user status (Active/Inactive)
  updateUserStatus(userId: string, statusRequest: UserUpdateStatusRequest): Observable<string> {
    return this.http.patch<string>(`${this.apiUrl}/usermanagement/status/${userId}`, statusRequest);
  }

  getRoles(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/role/roles`);
  }

  // Add user role
  addUserRole(userRoleRequest: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/userrole/add`, userRoleRequest);
  }

  // View user profile detail by email
  getUserProfileByEmail(email: string): Observable<ViewUserProfileResponse> {
     const params = new HttpParams().set('email', email);
    return this.http.get<ViewUserProfileResponse>(`${this.apiUrl}/usermanagement/viewProfile`, { params });
  }
  
  // Get user profile detail by userId
  getUserProfileById(userId: string): Observable<UserProfileResponse> {
    return this.http.get<UserProfileResponse>(`${this.apiUrl}/usermanagement/getProfile/${userId}`);
  }

 // Update user profile
  updateUserProfile(userId: string, updateUserProfileRequest: UpdateUserProfileRequest): Observable<string> {
    return this.http.put<string>(`${this.apiUrl}/usermanagement/editProfile/${userId}`, updateUserProfileRequest);
  }

  // Update user profile picture
  updateUserProfilePicture(userId: string, statusRequest: UserUpdateProfilePictureRequest): Observable<string> {
    return this.http.patch<string>(`${this.apiUrl}/usermanagement/profilePicture/${userId}`, statusRequest);
  }

  // Change user password
  changePassword(userId: string, changePasswordRequest: { currentPassword: string; newPassword: string }): Observable<string> {
    return this.http.put<string>(`${this.apiUrl}/usermanagement/changePassword/${userId}`, changePasswordRequest);
  }

  getImageAsBase64(url: string): Observable<string> {
    return this.http.get(url, { headers: new HttpHeaders({ 'X-Skip-Auth': 'true' }), responseType: 'blob' }).pipe(
      switchMap(blob => this.convertBlobToBase64(blob))
    );
  }

  private convertBlobToBase64(blob: Blob): Observable<string> {
    return new Observable(observer => {
      const reader = new FileReader();
      reader.onloadend = () => {
        observer.next(reader.result as string);
        observer.complete();
      };
      reader.readAsDataURL(blob);
    });
  }
}